import React, { useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import cx from "classnames";
import axios from "axios";
import { useForm } from "react-hook-form";
import { useHistory } from "react-router";
import { isNull, cloneDeep, isEmpty } from "lodash";
import ScrollToTop from "react-scroll-to-top";
import styles from "./Insurance.module.scss";
import { Navbar } from "./components/Navbar";
import { Layout } from "./components/Layout";
import { InsuranceForm } from "./components/InsuranceForm";
import { InsuranceFormData, RelationToPatient } from "./types";
import deleteIcon from "images/delete-icon.svg";
import addCircle from "images/add-circle.svg";
import errorIcon from "images/errorIcon.svg";
import { Card } from "components/Card";
import * as paths from "features/routing/paths";
import { getInsuranceCarriersRequest } from "api/getInsuranceCarriersRequest";
import { Button } from "components/Button";
import { useCurrentAppointment } from "features/patientDashboard/hooks/useCurrentAppointment";
import { useErrorToast } from "features/patientDashboard/hooks/useErrorToast";
import {
  setInsuranceData,
  setCurrentInsurance,
  setIsEditForm,
  setShowAddFooter,
  selectInsuranceData,
  selectCurrentInsurance,
  selectIsEditForm,
  selectShowAddFooter,
} from "features/insuranceDataSlice";
import {
  useCheckInPageTracking,
  useCheckInNavigationTracking,
  useCheckInTracking,
  useCheckInValidFieldTracking,
} from "features/checkIn/hooks/useCheckInTracking";

const CancelToken = axios.CancelToken;

export const Insurance: React.FC<{
  defaultValues: InsuranceFormData | null;
  onSubmit: (data: InsuranceFormData) => void;
}> = ({ defaultValues, onSubmit }) => {
  const [insuranceCarriers, setInsuranceCarriers] =
    React.useState<Array<string> | null>(null);
  useCheckInPageTracking({ step: 5, page: "apply_your_coverage" });
  useCheckInNavigationTracking({ step: 5, page: "apply_your_coverage" });
  const { trackCheckInEvent } = useCheckInTracking({
    step: 5,
    page: "apply_your_coverage",
  });
  const { currentAppointment } = useCurrentAppointment();
  const { showErrorToast } = useErrorToast();
  const history = useHistory();
  const [executeOnce, setExecuteOnce] = useState(false);
  const dispatch = useDispatch();
  const insuranceFormDataArr = useSelector(selectInsuranceData);
  const isShowFooter = useSelector(selectShowAddFooter);
  const [isForm, setIsForm] = useState(false);
  const [isFirstCard, setIsFirstCard] = useState(false);
  const [isSecondCard, setIsSecondCard] = useState(false);
  const [isErrorFirstFields, setErrorFirstFields] = useState(false);
  const [isErrorSecondFields, setErrorSecondFields] = useState(false);
  const [addAnotherCard, setAddAnotherCard] = useState(false);
  const [isFormRemove, setIsFormRemove] = useState(false);
  const currentInsurance = useSelector(selectCurrentInsurance);
  const isEditForm = useSelector(selectIsEditForm);
  const insuranceData = insuranceFormDataArr[currentInsurance];
  const [formValid, setFormValid] = useState(false);
  const errorMessageCard = "Enter Required Fields";

  const checkInsAddress = (insurance: InsuranceFormData) => {
    if (insurance.isPrimaryPolicyHolder === "no") {
      if (
        insurance.address1 &&
        insurance.state &&
        insurance.city &&
        insurance.zipCode &&
        insurance.subscriberBirthSex
      ) {
        return true;
      } else {
        return false;
      }
    } else {
      return true;
    }
  };

  const checkFormInsurance = (insurance: InsuranceFormData) => {
    const requiresAdditionalSubscriberDetails =
      insurance.isPrimaryPolicyHolder === "no";

    if (
      insurance &&
      insurance.insuranceCarrier &&
      insurance.subscriberFirstName &&
      insurance.isPrimaryPolicyHolder &&
      insurance.subscriberLastName &&
      insurance.subscriberId &&
      insurance.subscriberDateOfBirth &&
      insurance.isPrimaryPolicyHolder &&
      checkInsAddress(insurance) &&
      (insurance.insuranceCarrier !== "VSP" || insurance.subscriberSsn) &&
      (!requiresAdditionalSubscriberDetails || insurance.relationToPatient)
    ) {
      if (insurance.insuranceCarrier === "Other Insurance") {
        return insurance.otherInsuranceName ? true : false;
      } else {
        return true;
      }
    } else {
      return false;
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const getValidInsurance = useCallback((insuranceDataArr) => {
    // eslint-disable-next-line array-callback-return
    let isValidForm = false;
    let isValidFormTemp = false;
    let i = 0;
    for (const insurance of insuranceDataArr) {
      if (!isEmpty(insurance)) {
        isValidFormTemp = checkFormInsurance(insurance) ? true : false;
      }

      if (i === 0) {
        isValidForm = isValidFormTemp;
        setFormValid((prev) => (prev === isValidForm ? prev : isValidForm));
        setErrorFirstFields((prev) =>
          prev === isValidForm ? prev : isValidForm
        );
      } else {
        if (isValidForm)
          setFormValid((prev) => (prev === isValidForm ? prev : isValidForm));
        setErrorSecondFields((prev) =>
          prev === isValidFormTemp ? prev : isValidFormTemp
        );
      }
      i++;
    }
  }, []);

  React.useEffect(() => {
    //if (!currentAppointment) return;

    const cancelSource = CancelToken.source();

    const getCarriers = async () => {
      try {
        const carriers = await getInsuranceCarriersRequest({
          cancelToken: cancelSource.token,
        });
        setInsuranceCarriers(carriers);
      } catch (e) {
        // TODO: There is a race condition in axios's implementation of cancel tokens
        // that can cause the thrown object to not contain the relevant state. This should
        // be fixed in the next release of axios (see https://github.com/axios/axios/pull/3305)
        if (axios.isCancel(cancelSource.token.reason)) {
          return;
        }
        trackCheckInEvent("api_failure", {
          api: "get_insurance_carriers",
        });
        showErrorToast();
      }
    };

    getCarriers();

    return () => {
      cancelSource.cancel("UNMOUNTED");
    };
  }, [showErrorToast, setInsuranceCarriers, currentAppointment, trackCheckInEvent]);

  const resetForm = () => {
    setValue("insuranceCarrier", "");
    setValue("otherInsuranceName", "");
    setValue("subscriberFirstName", "");
    setValue("subscriberLastName", "");
    setValue("subscriberId", "");
    setValue("subscriberDateOfBirth", "");
    setValue("isPrimaryPolicyHolder", "");
    setValue("relationToPatient", undefined);
    setValue("subscriberBirthSex", "");
    setValue("address1", "");
    setValue("address2", "");
    setValue("state", "");
    setValue("city", "");
    setValue("zipCode", "");
    setValue("subscriberSsn", "");
  };

  const onNext = (data: InsuranceFormData) => {
    const insuranceDataArr = cloneDeep(insuranceFormDataArr);
    // eslint-disable-next-line array-callback-return
    [getValues()].map((insurance: InsuranceFormData) => {
      insuranceDataArr[currentInsurance] = createInsuranceObj(insurance);
    });
    dispatch(setInsuranceData(insuranceDataArr));
    let isFormValid = false;
    for (const insurance of insuranceDataArr) {
      if (!isEmpty(insurance)) {
        isFormValid = checkFormInsurance(insurance) ? true : false;
        if (!isFormValid) break;
      }
    }
    dispatch(setIsEditForm(true));
    if (isFormValid) {
      onSubmit(data);
      history.push(paths.checkInInsuranceReview());
    } else {
      trackCheckInEvent("validation_error", {
        reason: "saved_insurance_invalid",
      });
    }
  };

  const {
    register,
    handleSubmit,
    formState: { errors, isValid, isSubmitting },
    setValue,
    getValues,
    clearErrors,
    control,
    trigger,
    watch,
  } = useForm<Partial<InsuranceFormData>>({
    defaultValues: isNull(defaultValues) ? {} : {},
    mode: "onChange",
    reValidateMode: "onChange",
  });
  //useProgressGuard({ field: "insurance", isSubmitting });

  const insuranceFormId = "Checkin-InsuranceForm";
  const trackingInsuranceIndex = currentInsurance === 1 ? 2 : undefined;

  const checkFormEmpty = () => {
    return isEmpty(insuranceFormDataArr[0]) && isEmpty(insuranceFormDataArr[1]);
  };

  React.useEffect(() => {
    if (!isForm && insuranceData && !executeOnce) {
      setValue("insuranceCarrier", insuranceData?.insuranceCarrier);
      if (insuranceData?.insuranceCarrier === "Other Insurance")
        setValue("otherInsuranceName", insuranceData?.otherInsuranceName);
      setValue("subscriberFirstName", insuranceData?.subscriberFirstName);
      setValue("subscriberLastName", insuranceData?.subscriberLastName);
      setValue("subscriberId", insuranceData?.subscriberId);
      setValue("subscriberDateOfBirth", insuranceData?.subscriberDateOfBirth);
      setValue("isPrimaryPolicyHolder", insuranceData?.isPrimaryPolicyHolder);
      setValue("relationToPatient", insuranceData?.relationToPatient);
      setValue("subscriberSsn", insuranceData?.subscriberSsn);
      if (insuranceData?.isPrimaryPolicyHolder === "no") {
        setValue("subscriberBirthSex", insuranceData?.subscriberBirthSex);
        setValue("address1", insuranceData?.address1);
        setValue("address2", insuranceData?.address2);
        setValue("state", insuranceData?.state);
        setValue("city", insuranceData?.city);
        setValue("zipCode", insuranceData?.zipCode);
      }
      setExecuteOnce(true);
      if (!isEmpty(insuranceFormDataArr[0]) && currentInsurance === 1) {
        setIsFirstCard(true);
      } else {
        setIsFirstCard(false);
      }
      if (!isEmpty(insuranceFormDataArr[1]) && currentInsurance === 0) {
        setIsSecondCard(true);
      } else {
        setIsSecondCard(false);
      }
    }
    if (
      isShowFooter &&
      isEmpty(insuranceFormDataArr[0]) &&
      isEmpty(insuranceFormDataArr[1])
    ) {
      setAddAnotherCard(true);
    }
    if (
      isEditForm &&
      (isEmpty(insuranceFormDataArr[0]) || isEmpty(insuranceFormDataArr[1]))
    ) {
      setAddAnotherCard(true);
    }
    if (isEditForm) {
      isEmpty(insuranceFormDataArr[1])
        ? setIsFormRemove(false)
        : setIsFormRemove(true);
    }
    getValidInsurance(insuranceFormDataArr);
  }, [
    setValue,
    currentInsurance,
    insuranceFormDataArr,
    insuranceData,
    executeOnce,
    isShowFooter,
    isEditForm,
    isForm,
    getValidInsurance,
  ]);

  const refStr = "#";
  const getSubscriber = (index: number) => {
    let subscriberText = "";
    if (insuranceFormDataArr[index].subscriberFirstName)
      subscriberText += insuranceFormDataArr[index].subscriberFirstName;
    if (insuranceFormDataArr[index].subscriberLastName)
      subscriberText += " " + insuranceFormDataArr[index].subscriberLastName;
    if (insuranceFormDataArr[index].relationToPatient)
      subscriberText +=
        " (" + insuranceFormDataArr[index].relationToPatient + ")";

    return subscriberText;
  };

  const createInsuranceObj = (insurance: InsuranceFormData) => {
    if (isEmpty(insurance)) return {};
    return {
      subscriberFirstName: insurance.subscriberFirstName,
      subscriberLastName: insurance.subscriberLastName,
      insuranceCarrier: insurance.insuranceCarrier,
      otherInsuranceName: insurance.otherInsuranceName,
      subscriberId: insurance.subscriberId,
      subscriberDateOfBirth: insurance.subscriberDateOfBirth,
      isPrimaryPolicyHolder: insurance.isPrimaryPolicyHolder,
      subscriberSsn: insurance.subscriberSsn,
      relationToPatient:
        insurance.relationToPatient ||
        (insurance.isPrimaryPolicyHolder === "yes"
          ? RelationToPatient.Self
          : insurance.relationToPatient),
      subscriberBirthSex: insurance.subscriberBirthSex,
      address1: insurance.address1,
      address2: insurance.address2,
      state: insurance.state,
      city: insurance.city,
      zipCode: insurance.zipCode,
    };
  };

  const otherInsuranceCardsAreValid = insuranceFormDataArr.every(
    (insurance, index) =>
      index === currentInsurance ||
      isEmpty(insurance) ||
      checkFormInsurance(insurance)
  );
  const isNextButtonEnabled = isValid && otherInsuranceCardsAreValid;

  const cardShowHandler = (index: number, type: string) => {
    if (type !== "add") {
      trackCheckInEvent(type, {
        insuranceIndex: index + 1,
      });
    }
    clearErrors();
    if (type === "expand") {
      if (index === 1) {
        setIsFirstCard(true);
        setIsSecondCard(false);
      } else {
        setIsSecondCard(true);
        setIsFirstCard(false);
      }
      setAddAnotherCard(false);
      setIsFormRemove(true);
    } else if (type === "add") {
      setIsSecondCard(false);
      setIsFirstCard(true);
      setAddAnotherCard(false);
      setIsFormRemove(true);
    } else {
      setIsSecondCard(false);
      setIsFirstCard(false);
      setExecuteOnce(false);
      if (!checkFormEmpty()) setAddAnotherCard(true);
      setIsFormRemove(false);
    }
  };

  const goToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const isPrimaryPolicyHolder = watch("isPrimaryPolicyHolder");
  const insuranceCarrierValue = watch("insuranceCarrier");
  const subscriberFirstNameValue = watch("subscriberFirstName");
  const subscriberLastNameValue = watch("subscriberLastName");
  const subscriberDateOfBirthValue = watch("subscriberDateOfBirth");
  const subscriberIdValue = watch("subscriberId");
  const subscriberSsnValue = watch("subscriberSsn");
  const subscriberBirthSexValue = watch("subscriberBirthSex");
  const subscriberAddressValue = watch("address1");
  const subscriberCityValue = watch("city");
  const subscriberStateValue = watch("state");
  const subscriberZipValue = watch("zipCode");
  const relationToPatientValue = watch("relationToPatient");
  if (isPrimaryPolicyHolder && !isShowFooter) {
    dispatch(setShowAddFooter(true));
  }

  useCheckInValidFieldTracking({
    step: 5,
    page: "apply_your_coverage",
    action: "insurance_carrier",
    isValid: Boolean(insuranceCarrierValue),
    insuranceIndex: trackingInsuranceIndex,
  });
  useCheckInValidFieldTracking({
    step: 5,
    page: "apply_your_coverage",
    action: "subscriber_radio",
    isValid:
      isPrimaryPolicyHolder === "yes" || isPrimaryPolicyHolder === "no",
    choice: isPrimaryPolicyHolder,
    insuranceIndex: trackingInsuranceIndex,
  });
  useCheckInValidFieldTracking({
    step: 5,
    page: "apply_your_coverage",
    action: "subscriber_id",
    isValid: Boolean(subscriberIdValue?.toString().trim()),
    insuranceIndex: trackingInsuranceIndex,
  });
  useCheckInValidFieldTracking({
    step: 5,
    page: "apply_your_coverage",
    action: "subscriber_ssn_last_4",
    isValid:
      insuranceCarrierValue === "VSP" &&
      Boolean(subscriberSsnValue?.toString().trim()),
    insuranceIndex: trackingInsuranceIndex,
  });
  useCheckInValidFieldTracking({
    step: 5,
    page: "apply_your_coverage",
    action: "subscriber_first_name",
    isValid:
      isPrimaryPolicyHolder === "no" &&
      Boolean(subscriberFirstNameValue?.trim()),
    insuranceIndex: trackingInsuranceIndex,
  });
  useCheckInValidFieldTracking({
    step: 5,
    page: "apply_your_coverage",
    action: "subscriber_last_name",
    isValid:
      isPrimaryPolicyHolder === "no" &&
      Boolean(subscriberLastNameValue?.trim()),
    insuranceIndex: trackingInsuranceIndex,
  });
  useCheckInValidFieldTracking({
    step: 5,
    page: "apply_your_coverage",
    action: "subscriber_dob",
    isValid:
      isPrimaryPolicyHolder === "no" &&
      Boolean(subscriberDateOfBirthValue?.trim()),
    insuranceIndex: trackingInsuranceIndex,
  });
  useCheckInValidFieldTracking({
    step: 5,
    page: "apply_your_coverage",
    action: "subscriber_birth_sex",
    isValid:
      isPrimaryPolicyHolder === "no" &&
      Boolean(subscriberBirthSexValue?.trim()),
    insuranceIndex: trackingInsuranceIndex,
  });
  useCheckInValidFieldTracking({
    step: 5,
    page: "apply_your_coverage",
    action: "subscriber_address",
    isValid:
      isPrimaryPolicyHolder === "no" &&
      Boolean(subscriberAddressValue?.trim()) &&
      Boolean(subscriberCityValue?.trim()) &&
      Boolean(subscriberStateValue) &&
      Boolean(subscriberZipValue?.trim()),
    insuranceIndex: trackingInsuranceIndex,
  });
  useCheckInValidFieldTracking({
    step: 5,
    page: "apply_your_coverage",
    action: "subscriber_relation",
    isValid:
      isPrimaryPolicyHolder === "no" && Boolean(relationToPatientValue),
    insuranceIndex: trackingInsuranceIndex,
  });

  const handleTrackedNext = handleSubmit(
    (data) => {
      trackCheckInEvent("next", {
        ...(trackingInsuranceIndex
          ? { insuranceIndex: trackingInsuranceIndex }
          : {}),
      });
      onNext(data);
    },
    () => {
      trackCheckInEvent("validation_error", {
        ...(trackingInsuranceIndex
          ? { insuranceIndex: trackingInsuranceIndex }
          : {}),
      });
    }
  );

  return (
    <React.Fragment>
      <Layout
        navbar={<Navbar currentStep={1} totalSteps={2} title={"Insurance"} />}
        className={`${
          isFirstCard
            ? styles.expandLayoutIn
            : isSecondCard
            ? styles.expandLayoutOut
            : styles.expandLayoutNone
        } ${
          !isErrorFirstFields && isFirstCard ? styles.expandLayoutInError : ""
        }

          ${
            !isErrorSecondFields && isSecondCard
              ? styles.expandLayoutOutError
              : ""
          }
        }`}
        primaryCta={
          <Button
            onClick={handleTrackedNext}
            renderAsDisabled={!isNextButtonEnabled}
            text="Next"
            type="submit"
            form={insuranceFormId}
          />
        }
        emptyHeight={true}
      >
        {isFirstCard && (
          <div>
            <Card className={styles.cardExpand}>
              <div className={styles.innerCard}>
                <div className={styles.divider}>
                  <div className={styles.headLabel}>Insurance Carrier</div>
                  {insuranceFormDataArr[0]?.insuranceCarrier}
                </div>
                <div className={styles.divider}>
                  <div className={styles.headLabel}>Subscriber</div>
                  {getSubscriber(0)}
                </div>
                <button
                  className={styles.expandBtn}
                  onClick={() => {
                    const insuranceDataArr = cloneDeep(insuranceFormDataArr);
                    // eslint-disable-next-line array-callback-return
                    [getValues()].map((insurance: InsuranceFormData) => {
                      insuranceDataArr[1] = createInsuranceObj(insurance);
                    });
                    dispatch(setCurrentInsurance(0));
                    dispatch(setInsuranceData(insuranceDataArr));
                    getValidInsurance(insuranceDataArr);
                    setExecuteOnce(false);
                    setIsForm(false);
                    cardShowHandler(0, "expand");
                    goToTop();
                  }}
                >
                  Expand
                </button>
              </div>
              {!isErrorFirstFields && (
                <div className={styles.errorFields}>
                  <img src={errorIcon} alt="Error" />
                  <p>{errorMessageCard}</p>
                </div>
              )}
              <a
                className={styles.removeExpand}
                href={refStr}
                onClick={(e) => {
                  const insuranceArrTemp = cloneDeep(insuranceFormDataArr);
                  insuranceArrTemp[0] = insuranceArrTemp[1];
                  insuranceArrTemp[1] = {};
                  dispatch(setInsuranceData(insuranceArrTemp));
                  dispatch(setCurrentInsurance(0));
                  setIsForm(false);
                  cardShowHandler(0, "remove");
                  goToTop();
                  e.preventDefault();
                }}
              >
                <img src={deleteIcon} alt="Remove" />
                Remove
              </a>
            </Card>
            <div
              className={`${styles.mobileExpand} ${
                !isErrorFirstFields ? styles.errorExpand : ""
              }`}
            >
              <div className={styles.innerLeft}>
                <span>Insurance 1</span>
                <a
                  href={refStr}
                  onClick={() => {
                    const insuranceDataArr = cloneDeep(insuranceFormDataArr);
                    // eslint-disable-next-line array-callback-return
                    [getValues()].map((insurance: InsuranceFormData) => {
                      insuranceDataArr[1] = createInsuranceObj(insurance);
                    });
                    dispatch(setCurrentInsurance(0));
                    dispatch(setInsuranceData(insuranceDataArr));
                    getValidInsurance(insuranceDataArr);
                    setExecuteOnce(false);
                    setIsForm(false);
                    cardShowHandler(0, "expand");
                    goToTop();
                  }}
                >
                  Expand
                </a>
              </div>
              <div className={styles.innerRight}>
                <a
                  href={refStr}
                  onClick={(e) => {
                    const insuranceArrTemp = cloneDeep(insuranceFormDataArr);
                    insuranceArrTemp[0] = insuranceArrTemp[1];
                    insuranceArrTemp[1] = {};
                    dispatch(setInsuranceData(insuranceArrTemp));
                    dispatch(setCurrentInsurance(0));
                    setIsForm(false);
                    cardShowHandler(0, "remove");
                    goToTop();
                    e.preventDefault();
                  }}
                >
                  <img src={deleteIcon} alt="Remove" />
                </a>
              </div>
              {!isErrorFirstFields && (
                <div className={styles.errorFieldsMobile}>
                  <img src={errorIcon} alt="Error" />
                  <p>{errorMessageCard}</p>
                </div>
              )}
            </div>
          </div>
        )}
        <InsuranceForm
          register={register}
          control={control}
          insuranceCarriers={insuranceCarriers}
          isRemoveBtn={false}
          setValue={setValue}
          errors={errors}
          watch={watch}
          onSubmit={handleTrackedNext}
          formId={insuranceFormId}
        />
        {isFormRemove && (
          <div className={styles.removeForm}>
            <br />
            <a
              className={styles.removeExpand}
              href={refStr}
              onClick={(e) => {
                const insuranceArrTemp = cloneDeep(insuranceFormDataArr);
                const currentIndex = cloneDeep(currentInsurance);
                if (currentIndex === 1) {
                  insuranceArrTemp[1] = {};
                } else if (currentIndex === 0) {
                  insuranceArrTemp[0] = insuranceArrTemp[1];
                  insuranceArrTemp[1] = {};
                }
                dispatch(setInsuranceData(insuranceArrTemp));
                dispatch(setCurrentInsurance(0));
                setIsForm(false);
                cardShowHandler(currentIndex, "inFormRemove");
                goToTop();
                e.preventDefault();
              }}
            >
              <img src={deleteIcon} alt="Remove" />
              Remove
            </a>
          </div>
        )}
        {addAnotherCard && (
          <div className={styles.insurance}>
            <a
              className={styles.addInsurance}
              href={refStr}
              onClick={(e) => {
                e.preventDefault();
                trackCheckInEvent("add_another_insurance", {
                  insuranceIndex: 2,
                });
                if (isValid) {
                  setErrorFirstFields(true);
                  const insuranceDataArr = cloneDeep(insuranceFormDataArr);
                  // eslint-disable-next-line array-callback-return
                  [getValues()].map((insurance: InsuranceFormData) => {
                    insuranceDataArr[0] = createInsuranceObj(insurance);
                  });
                  dispatch(setIsEditForm(false));
                  dispatch(setCurrentInsurance(1));
                  dispatch(setInsuranceData(insuranceDataArr));
                  dispatch(setShowAddFooter(false));
                  resetForm();
                  cardShowHandler(1, "add");
                  goToTop();
                } else {
                  trigger();
                }
              }}
            >
              <h1 className={styles.header}>
                <img src={addCircle} alt="Add" />
                ADD ANOTHER INSURANCE
              </h1>
              <span className={styles.addSpan}>
                MyEyeDr. will check and apply any additional Vision or Medical
                that gives <br /> you the greatest savings benefit
              </span>
            </a>
          </div>
        )}
        {isSecondCard && (
          <div>
            <Card className={styles.cardExpand2}>
              <div className={styles.innerCard}>
                <div className={styles.divider}>
                  <div className={styles.headLabel}>Insurance Carrier</div>
                  {insuranceFormDataArr[1]?.insuranceCarrier}
                </div>
                <div className={styles.divider}>
                  <div className={styles.headLabel}>Subscriber</div>
                  {getSubscriber(1)}
                </div>
                <button
                  className={cx(styles.divider, styles.expandBtn)}
                  onClick={() => {
                    dispatch(setCurrentInsurance(1));
                    setExecuteOnce(false);
                    setIsForm(false);
                    const insuranceDataArr = cloneDeep(insuranceFormDataArr);
                    // eslint-disable-next-line array-callback-return
                    [getValues()].map((insurance: InsuranceFormData) => {
                      insuranceDataArr[0] = createInsuranceObj(insurance);
                    });
                    dispatch(setInsuranceData(insuranceDataArr));
                    getValidInsurance(insuranceDataArr);
                    cardShowHandler(1, "expand");
                    goToTop();
                  }}
                >
                  Expand
                </button>
              </div>
              {!isErrorSecondFields && (
                <div className={styles.errorFields}>
                  <img src={errorIcon} alt="Error" />
                  <p>{errorMessageCard}</p>
                </div>
              )}
              <a
                className={styles.removeExpand}
                href={refStr}
                onClick={(e) => {
                  const insuranceArrTemp = cloneDeep(insuranceFormDataArr);
                  insuranceArrTemp[1] = {};
                  //delete insuranceArrTemp[currentIndex];
                  dispatch(setInsuranceData(insuranceArrTemp));
                  dispatch(setCurrentInsurance(0));
                  setIsForm(false);
                  cardShowHandler(1, "remove");
                  goToTop();
                  e.preventDefault();
                }}
              >
                <img src={deleteIcon} alt="Remove" />
                Remove
              </a>
            </Card>
            <div
              className={`${styles.mobileExpand} ${styles.secondExpand} ${
                !isErrorSecondFields ? styles.errorExpand : ""
              }`}
            >
              <div className={styles.innerLeft}>
                <span>Insurance 2</span>
                <a
                  href={refStr}
                  onClick={() => {
                    dispatch(setCurrentInsurance(1));
                    setExecuteOnce(false);
                    setIsForm(false);
                    const insuranceDataArr = cloneDeep(insuranceFormDataArr);
                    // eslint-disable-next-line array-callback-return
                    [getValues()].map((insurance: InsuranceFormData) => {
                      insuranceDataArr[0] = createInsuranceObj(insurance);
                    });
                    dispatch(setInsuranceData(insuranceDataArr));
                    getValidInsurance(insuranceDataArr);
                    cardShowHandler(1, "expand");
                    goToTop();
                  }}
                >
                  Expand
                </a>
              </div>
              <div className={styles.innerRight}>
                <a
                  href={refStr}
                  onClick={(e) => {
                    const insuranceArrTemp = cloneDeep(insuranceFormDataArr);
                    insuranceArrTemp[1] = {};
                    //delete insuranceArrTemp[currentIndex];
                    dispatch(setInsuranceData(insuranceArrTemp));
                    dispatch(setCurrentInsurance(0));
                    setIsForm(false);
                    cardShowHandler(1, "remove");
                    goToTop();
                    e.preventDefault();
                  }}
                >
                  <img src={deleteIcon} alt="Remove" />
                </a>
              </div>
              {!isErrorSecondFields && (
                <div className={styles.errorFieldsMobile}>
                  <img src={errorIcon} alt="Error" />
                  <p>{errorMessageCard}</p>
                </div>
              )}
            </div>
          </div>
        )}
        <ScrollToTop smooth className={styles.hideScroll} />
      </Layout>
    </React.Fragment>
  );
};
